import _getNearestColor from "../utility/getNearestColor.js";
import _escapeHtmlEntities from "../events/escapeHtmlEntities.js";
import { decode as _decodeHtml } from "html-entities";
import _convertToHtml from "../quill/convertToHtml.js";
import _convertToPlainText from "../quill/convertToPlainText.js";
import _flexbox from "../../dict/flexbox.js";
import _Event from "../../classes/event.js";
import _tcLib from "../../lib/timecode.js";
const tcLib = _tcLib;
const Event = _Event;
const flexbox = _flexbox;
const convertToPlainText = _convertToPlainText;
const convertToHtml = _convertToHtml;
const decodeHtml = _decodeHtml;
const escapeHtmlEntities = _escapeHtmlEntities;
const getNearestColor = _getNearestColor;
export default {
  frameRateMap: {
    "23.976": "24",
    "24": "24",
    "25": "25",
    "29.97": "30",
    "30": "30",
    "50": "50",
    "59.94": "60",
    "60": "60"
  },
  frameRateMultiplierMap: {
    "23.976": "999 1000",
    "24": "1 1",
    "25": "1 1",
    "29.97": "999 1000",
    "30": "1 1",
    "50": "1 1",
    "59.94": "999 1000",
    "60": "1 1"
  },
  buildStyleMap: function (head) {
    let styleMap = {};
    if (!head || !head.styling || !head.styling.style) {
      return styleMap;
    }
    let styles = Array.isArray(head.styling.style) ? head.styling.style : [head.styling.style];
    styles.forEach(style => {
      if (!style) {
        return;
      }
      let id = style["@_xml:id"];
      if (!id) {
        return;
      }
      let color = null;
      let rawColor = style["@_tts:color"];
      if (rawColor) {
        try {
          let nearestColor = getNearestColor(rawColor);
          if (nearestColor && nearestColor.name) {
            color = nearestColor.name;
          }
        } catch (err) {
          //Ignore unparseable colors; leave color unset
        }
      }

      //Treat white as the default text color so we don't wrap every event in a color tag
      if (color === "white") {
        color = null;
      }
      styleMap[id] = {
        bold: style["@_tts:fontWeight"] === "bold",
        italic: style["@_tts:fontStyle"] === "italic",
        underline: typeof style["@_tts:textDecoration"] === "string" && style["@_tts:textDecoration"].indexOf("underline") !== -1,
        color: color
      };
    });
    return styleMap;
  },
  //Return the open/close html tag pair for a resolved style definition
  styleToTags: function (styleDef) {
    let open = "",
      close = "";
    if (!styleDef) {
      return {
        open,
        close
      };
    }

    //Color is the outermost wrapper, then bold, italic, underline
    if (styleDef.color) {
      open += `<${styleDef.color}>`;
      close = `</${styleDef.color}>` + close;
    }
    if (styleDef.bold) {
      open += "<strong>";
      close = "</strong>" + close;
    }
    if (styleDef.italic) {
      open += "<em>";
      close = "</em>" + close;
    }
    if (styleDef.underline) {
      open += "<u>";
      close = "</u>" + close;
    }
    return {
      open,
      close
    };
  },
  decodeCaptionBlock: function (captionBlock, frameRate, dropFrame, useClockTime, styleMap, defaultStyleId, defaultRegion) {
    let offset = false,
      events = [];
    if (captionBlock['@_begin']) {
      offset = captionBlock['@_begin'];
    } else {
      offset = false;
    }
    if (Array.isArray(captionBlock.p)) {
      captionBlock.p.forEach(captionEvent => {
        events.push(this.decodeCaptionEvent(captionEvent, frameRate, dropFrame, offset, useClockTime, styleMap, defaultStyleId, defaultRegion));
      });
    } else {
      let captionEvent = captionBlock.p;
      events.push(this.decodeCaptionEvent(captionEvent, frameRate, dropFrame, offset, useClockTime, styleMap, defaultStyleId, defaultRegion));
    }
    return events;
  },
  decodeCaptionEvent: function (captionEvent, frameRate, dropFrame, offset, useClockTime, styleMap, defaultStyleId, defaultRegion) {
    //console.log(captionEvent);
    let start = tcLib.createTc(captionEvent["@_begin"], frameRate, dropFrame);
    let end = tcLib.createTc(captionEvent["@_end"], frameRate, dropFrame);
    if (offset) {
      if (offset.charAt(0) == "-") {
        start.subtract(offset.substring(1));
        end.subtract(offset.substring(1));
      } else {
        start.add(offset);
        end.add(offset);
      }
    }

    //console.log(start.toString(),end.toString());
    let text = this.decodeCaptionText(captionEvent["#text"], styleMap, defaultStyleId);
    //Fall back to the body level default region when the event does not define its own
    let regionId = captionEvent["@_region"] !== undefined ? captionEvent["@_region"] : defaultRegion;
    let region = flexbox.alignmentMap[regionId] || "end";
    return new Event({
      start: tcLib.parseTcToSec(start.toString(), frameRate, dropFrame, useClockTime),
      end: tcLib.parseTcToSec(end.toString(), frameRate, dropFrame, useClockTime),
      yPos: region,
      text: text
    });
  },
  decodeCaptionText: function (text, styleMap, defaultStyleId) {
    if (!text) {
      return "";
    }
    let html = this.formatText(text, styleMap, defaultStyleId);
    return convertToHtml(html);
  },
  formatText: function (text, styleMap, defaultStyleId) {
    if (!text) {
      return "";
    }
    text = text.toString();

    // Handle spans that contain break tags by preserving span attributes across breaks
    text = this.preserveSpansAcrossBreaks(text);

    // Now split by breaks and process each line
    let segments = text.split(/<br\/>|<br>|<br \/>/).map(segment => {
      //Collapse source-formatting whitespace (newlines/indentation inside spans) to a single
      //space so it is not later interpreted as a line break. Line breaks come from <br/> only.
      segment = segment.replace(/\s+/g, " ");
      return this.replaceSpanTagsWithFormatTags(segment, styleMap, defaultStyleId).trim();
    });
    let fText = segments.join("\n");
    return decodeHtml(fText.trim());
  },
  preserveSpansAcrossBreaks: function (text) {
    // Find span tags that contain break tags and split them appropriately
    const spanWithBreakRegex = /<span([^>]*)>((?:[^<]|<(?!\/span>|br\/?>))*?)<br\s*\/?>((?:[^<]|<(?!\/span>))*?)<\/span>/gi;
    let result = text;
    let match;

    // Keep processing until no more spans with breaks are found
    while ((match = spanWithBreakRegex.exec(result)) !== null) {
      const fullMatch = match[0];
      const attributes = match[1];
      const beforeBreak = match[2];
      const afterBreak = match[3];

      // Create separate spans for content before and after the break
      const replacement = `<span${attributes}>${beforeBreak}</span><br/><span${attributes}>${afterBreak}</span>`;
      result = result.replace(fullMatch, replacement);

      // Reset regex to start from beginning since we modified the string
      spanWithBreakRegex.lastIndex = 0;
    }
    return result;
  },
  replaceSpanTagsWithFormatTags: function (str, styleMap, defaultStyleId) {
    //Look after just the italic tags
    if (!str) {
      return "";
    }

    //Apply the body level default style to the bare text (text outside of any span) first,
    //so that text inside an explicit span (even one that resolves to "normal") is never
    //affected by the default.
    str = this.applyDefaultStyle(str, styleMap, defaultStyleId);

    //Resolve <span style="ID"> references against the head <styling> definitions.
    str = this.resolveStyleIdSpans(str, styleMap);

    //Fallback handling for ITT files that use inline tts:* attributes rather than style ids.
    str = this.replaceNestedItalicSpans(str);
    str = this.transformTTMLColors(str);

    // Replace <span> tags with italic and underline attributes
    //console.log(str);
    str = str.replace(/<span\s+tts:fontStyle\s*=\s*(['"]?)italic\1\s+tts:textDecoration\s*=\s*(['"]?)underline\2\s*>(.*?)<\/span>/gmi, '<em><u>$3</u></em>');

    // Replace <span> tags with italic attribute
    str = str.replace(/<span\s+tts:fontStyle\s*=\s*(['"]?)italic\1\s*>(.*?)<\/span>/gmi, '<em>$2</em>');

    // Replace <span> tags with italic attribute
    str = str.replace(/<span\s+style\s*=\s*(['"]?)italic\1\s*>(.*?)<\/span>/gi, '<em>$2</em>');

    // Replace <span> tags with underline attribute
    str = str.replace(/<span\s+tts:textDecoration\s*=\s*(['"]?)underline\1\s*>(.*?)<\/span>/gmi, '<u>$2</u>');
    str = str.replace(/<span[^>]*>(.*?)<\/span>/gmi, '$1');
    str = str.replace(/<\/?span[^>]*>/gmi, "");
    //console.log(str);

    str = this.removeRedundantTags(str);
    return str;
  },
  //Resolve <span style="ID"> references against the head <styling> definitions, innermost first.
  resolveStyleIdSpans: function (str, styleMap) {
    if (!str || !styleMap) {
      return str;
    }

    //Match an innermost style-id span (its content contains no nested span tags).
    //The id is limited to non-quote characters so it cannot backtrack across a nested span.
    const regex = /<span\s+style\s*=\s*(['"])([^'"]*)\1[^>]*>((?:(?!<\/?span)[\s\S])*?)<\/span>/gi;
    let result = str;
    let previousResult;
    let guard = 0;
    do {
      previousResult = result;
      result = result.replace(regex, (match, quote, id, content) => {
        let styleDef = styleMap[id];
        if (!styleDef) {
          //Unknown id: leave untouched so the inline fallbacks / cleanup can handle it.
          return match;
        }
        let tags = this.styleToTags(styleDef);
        return tags.open + content + tags.close;
      });
      guard++;
    } while (result !== previousResult && guard < 100);
    return result;
  },
  //Wrap the top level bare text (text outside of any span) in the body default style's tags.
  applyDefaultStyle: function (str, styleMap, defaultStyleId) {
    if (!str || !styleMap || !defaultStyleId) {
      return str;
    }
    let styleDef = styleMap[defaultStyleId];
    if (!styleDef) {
      return str;
    }
    let tags = this.styleToTags(styleDef);
    //Nothing to do when the default style has no formatting (e.g. "normal").
    if (!tags.open) {
      return str;
    }
    let result = "";
    let buffer = "";
    let depth = 0;
    const flush = () => {
      if (!buffer) {
        return;
      }
      if (buffer.trim().length > 0) {
        let leading = buffer.match(/^\s*/)[0];
        let trailing = buffer.match(/\s*$/)[0];
        let core = buffer.slice(leading.length, buffer.length - trailing.length);
        //Keep surrounding whitespace outside of the formatting tags.
        result += leading + tags.open + core + tags.close + trailing;
      } else {
        result += buffer;
      }
      buffer = "";
    };
    let i = 0;
    while (i < str.length) {
      if (str[i] === "<") {
        let close = str.indexOf(">", i);
        if (close === -1) {
          if (depth === 0) {
            buffer += str.slice(i);
          } else {
            result += str.slice(i);
          }
          break;
        }
        let tag = str.slice(i, close + 1);
        let isSpanOpen = /^<span(\s|>)/i.test(tag);
        let isSpanClose = /^<\/span\s*>/i.test(tag);
        if (depth === 0) {
          //Any tag at the top level ends the current bare-text run.
          flush();
        }
        result += tag;
        if (isSpanOpen) {
          depth += 1;
        } else if (isSpanClose) {
          depth = Math.max(0, depth - 1);
        }
        i = close + 1;
      } else {
        if (depth === 0) {
          buffer += str[i];
        } else {
          result += str[i];
        }
        i++;
      }
    }
    flush();
    return result;
  },
  replaceNestedItalicSpans: function (input) {
    const regex = /<span tts:fontStyle="italic">((?:[^<]|<(?!span tts:fontStyle="italic"))*(?:<span tts:fontStyle="italic">(?:[^<]|<(?!span tts:fontStyle="italic"))*<\/span>)*(?:[^<]|<(?!span tts:fontStyle="italic"))*)<\/span>/g;
    let result = input;
    let previousResult;
    do {
      previousResult = result;
      result = result.replace(regex, '<em>$1</em>');
    } while (result !== previousResult);
    return result;
  },
  removeRedundantTags: function (input) {
    const tags = ['em', 'u']; // Add more tags here if needed
    let result = input;
    for (const tag of tags) {
      const regex = new RegExp(`<${tag}>((?:<${tag}>.*?<\/${tag}>|.)*?)<\/${tag}>`, 'g');
      let previousResult;
      do {
        previousResult = result;
        result = result.replace(regex, (match, content) => {
          // Remove inner tags of the same type
          content = content.replace(new RegExp(`<${tag}>|<\/${tag}>`, 'g'), '');
          return `<${tag}>${content}</${tag}>`;
        });
      } while (result !== previousResult);
    }
    return result;
  },
  replaceNestedItalicSpans: function (input) {
    const regex = /<span tts:fontStyle="italic">((?:[^<]|<(?!span tts:fontStyle="italic"))*(?:<span tts:fontStyle="italic">(?:[^<]|<(?!span tts:fontStyle="italic"))*<\/span>)*(?:[^<]|<(?!span tts:fontStyle="italic"))*)<\/span>/g;
    let result = input;
    let previousResult;
    do {
      previousResult = result;
      result = result.replace(regex, '<em>$1</em>');
    } while (result !== previousResult);
    return result;
  },
  transformTTMLColors: function (input) {
    if (!input || typeof input !== 'string') {
      throw new Error('Input must be a non-empty string');
    }

    // Regular expression to match TTML spans with any attributes
    // Captures three groups:
    // 1. Any attributes before tts:color
    // 2. The color value
    // 3. Any attributes after tts:color
    // 4. The content inside the span
    const ttmlColorRegex = /<span\s+((?:[^>]*\s+)?)?tts:color="([^"]+)"(\s+[^>]*)?>[\s\n]*([\s\S]*?)[\s\n]*<\/span>/g;

    // Transform the input string
    const transformed = input.replace(ttmlColorRegex, (match, preAttrs, color, postAttrs, content) => {
      if (!color) {
        console.warn('Empty color value found in TTML metadata');
        return match;
      }

      // Combine all non-color attributes
      const otherAttrs = [preAttrs, postAttrs].filter(Boolean).join(' ').replace(/\s+tts:color="[^"]*"\s*/, ' ').trim();
      try {
        let nearestColor = getNearestColor(color);
        if (nearestColor) {
          color = nearestColor.name;
        }
      } catch (err) {
        console.log(err.message);
      }

      // Create the new format, preserving other attributes
      return `<span${otherAttrs ? ' ' + otherAttrs : ''}><${color}>${content}</${color}></span>`;
    });
    return transformed;
  },
  convertToItt: function (html) {
    return escapeHtmlEntities(html).replace(/(<strong>)+/gmi, "").replace(/(<\/strong>)+/gmi, "").replace(/(<em><u>)+/gmi, "<span style='italic'>").replace(/(<\/em><\/u>)+/gmi, "</span>").replace(/(<em>)+/gmi, "<span style='italic'>").replace(/(<\/em>)+/gmi, "</span>").replace(/(<u>)+/gmi, "<span style='underline'>").replace(/(<\/u>)+/gmi, "</span>").replace(/(<\/span>)+/g, "</span>");
  }
};